import { startCase } from "lodash";
import NextHead from "next/head";
import { Fragment, FunctionComponent, ReactNode } from "react";

// refer to these docs: https://ogp.me/#types
export enum OgTypes {
  ARTICLE = "article",
  BOOK = "book",
  MUSIC = "music",
  PROFILE = "profile",
  VIDEO = "video",
  WEBSITE = "website"
}

export interface OwnProps {
  children?: ReactNode;
  description?: string;
  ogImage: string;
  ogImageAlt?: string;
  ogType?: OgTypes;
  ogUrl: string;
  title: string;
  twitterImage?: string;
  twitterSite?: string;
  twitterTitle?: string;
}

/**
 * open Graph protocol specifies the use of `property` and `content` attributes
 * for markup (<meta property="og:image" content="url"/>) while Twitter cards
 * use `name` and `content`
 */

/**
 * To avoid duplicate tags in your head you can use the `key` property,
 * which will make sure the tag is only rendered once
 */

const Head: FunctionComponent<OwnProps> = ({
  ogType = OgTypes.WEBSITE,
  ...props
}) => (
  <NextHead>
    <title>{startCase(props.title)}</title>
    {props.twitterImage && (
      <Fragment>
        <meta
          content="summary_large_image"
          key="twitter:card"
          name="twitter:card"
        />
        <meta
          content={props.twitterImage}
          key="twitter:image"
          name="twitter:image"
        />
      </Fragment>
    )}
    {props.twitterSite && (
      <meta
        content={props.twitterSite}
        key="twitter:site"
        name="twitter:site"
      />
    )}
    {props.twitterTitle && (
      <meta
        content={props.twitterTitle}
        key="twitter:title"
        name="twitter:title"
      />
    )}
    {props.description && (
      <Fragment>
        <meta
          content={props.description}
          key="description"
          name="description"
        />
        <meta
          content={props.description}
          key="twitter:description"
          name="twitter:description"
        />
        <meta
          content={props.description}
          key="og:description"
          property="og:description"
        />
      </Fragment>
    )}
    {props.ogImage && (
      <Fragment>
        <meta content={props.ogImage} key="og:image" property="og:image" />
        <meta content="1200" key="og:image:width" property="og:image:width" />
        <meta content="630" key="og:image:height" property="og:image:height" />
      </Fragment>
    )}
    {props.ogImageAlt && (
      <meta
        content={props.ogImageAlt}
        key="og:image:alt"
        property="og:image:alt"
      />
    )}
    <meta content={startCase(props.title)} key="og:title" property="og:title" />
    {props.ogUrl && (
      <meta content={props.ogUrl} key="og:url" property="og:url" />
    )}
    <meta content={ogType} key="og:type" property="og:type" />
    {props.children}
  </NextHead>
);

export default Head;
